import React, { useState, useEffect, useCallback } from 'react';
import classNames from 'classnames';
import { debounce } from 'debounce';

const DEBOUNCE_TIME = 500; // ms
const SERIE_PREFIX = 'JS';

type TSearchBar = {
    customPlaceHolder?: string;
    filterKey: string;
    onFilterChange: (
        id: string,
        {
            value,
        }: {
            value: string;
        },
    ) => void;
    currentUrlParams: URLSearchParams;
    isSearchBySeries?: boolean;
    value?: string;
    inputType?: string;
    maxInputLength?: number;
};

export default function SearchBar({
    customPlaceHolder,
    filterKey,
    onFilterChange,
    currentUrlParams,
    isSearchBySeries,
    inputType = 'text',
    maxInputLength = undefined,
}: TSearchBar) {
    const [searchValue, setSearchValue] = useState('');
    const [isSearchBarWhite, setIsSearchBarWhite] = useState(false);
    const [isSearchBarTransparent, setIsSearchBarTransparent] = useState(true);

    useEffect(() => {
        const searchParam = currentUrlParams.get(filterKey);
        if (searchParam && searchParam !== searchValue) {
            setSearchValue(searchParam);
        }
    }, [window.location.pathname]);

    const debouncedFunc = useCallback(
        debounce((searchString: string) => {
            onFilterChange(filterKey, {
                value: searchString,
            });
        }, DEBOUNCE_TIME),
        [],
    );

    useEffect(
        () =>
            function cleanup() {
                debouncedFunc.clear();
            },
        [],
    );

    const checkIfSerieIsPositiveNumber = (searchString: string) => {
        const serieWithoutPrefix = searchString
            .toUpperCase()
            .replace(SERIE_PREFIX, '');
        const serieNumber = parseInt(serieWithoutPrefix, 10);

        return serieNumber > 0;
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const searchString = event.target.value;

        setSearchValue(searchString);
        if (
            !isSearchBySeries ||
            searchString === '' ||
            checkIfSerieIsPositiveNumber(searchString)
        ) {
            debouncedFunc(searchString);
        }
    };

    const handleFocus = () => {
        if (!isSearchBarWhite) {
            setIsSearchBarWhite(true);
            setIsSearchBarTransparent(false);
        }
    };

    const handleBlur = () => {
        setIsSearchBarWhite(false);
        setIsSearchBarTransparent(true);
    };

    const searchBarClassNames = classNames({
        'fr-search-bar-wrapper': true,
        searchBarWhite: isSearchBarWhite,
        searchBarTransparent: isSearchBarTransparent,
    });

    return (
        <div className={searchBarClassNames}>
            <input
                className="fr-search-bar"
                type={inputType}
                value={searchValue}
                placeholder={customPlaceHolder || 'Search...'}
                onChange={handleChange}
                onFocus={handleFocus}
                onBlur={handleBlur}
                maxLength={maxInputLength}

            />
        </div>
    );
}
